// "New payment request" dialog: organizer -> event (its payout account) ->
// payer -> amount + Feis.link fee -> what it's for -> optional pay-by date.
// Sending creates the request and emails the payer a link (backend).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Msr } from '../OireachtasPanel/symbols';
import { createRequest, organizerEvents, searchOrganizers, searchPayers } from './api';
import useDialogFocus from './useDialogFocus';
import {
  EVENT_TYPE_LABELS,
  NOTE_MAX,
  centsToInput,
  defaultFeeCents,
  dueDay,
  emptyForm,
  errText,
  eventKeyOf,
  feeRuleOf,
  money,
  parseDollarsToCents,
  previewTotals,
  validateForm,
} from './paymentRequestModel';

const Field = ({ label, error, hint, children, htmlFor, id }) => (
  <div className={`ds-field${error ? ' bad' : ''}`} id={id}>
    <label className="ds-label" htmlFor={htmlFor}>{label}</label>
    {children}
    {error ? <div className="pc-err" role="alert">{error}</div> : hint ? <div className="ds-hint">{hint}</div> : null}
  </div>
);

// Search-as-you-type for organizers / payers. `search(q)` resolves to a list.
function useSearch(search, query, { minLength = 0, delay = 300, enabled = true } = {}) {
  const [state, setState] = useState({ items: [], loading: false, error: '' });
  const seq = useRef(0);
  useEffect(() => {
    const q = query.trim();
    if (!enabled || q.length < minLength) {
      seq.current += 1;
      setState({ items: [], loading: false, error: '' });
      return undefined;
    }
    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: '' }));
    const t = setTimeout(async () => {
      try {
        const items = await search(q);
        if (mine === seq.current) setState({ items, loading: false, error: '' });
      } catch (e) {
        if (mine === seq.current) setState({ items: [], loading: false, error: errText(e) });
      }
    }, delay);
    return () => clearTimeout(t);
  }, [search, query, minLength, delay, enabled]);
  return state;
}

const Picked = ({ icon, title, sub, onChange, label, id }) => (
  <div className="pr-pick">
    <Msr>{icon}</Msr>
    <div className="pr-who">
      <b>{title}</b>
      {sub && <span>{sub}</span>}
    </div>
    <button type="button" id={id} onClick={onChange} aria-label={`Change ${label}`}>Change</button>
  </div>
);

function Picker({ id, label, placeholder, query, setQuery, state, onPick, render, emptyText, minLength = 0, autoFocus }) {
  const short = query.trim().length < minLength;
  return (
    <>
      <div className="op-search pr-search">
        <Msr>search</Msr>
        <input
          id={id}
          type="search"
          placeholder={placeholder}
          aria-label={label}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-autofocus={autoFocus ? true : undefined}
          autoComplete="off"
        />
      </div>
      {!short && (
        <div className="pr-results" role="listbox" aria-label={`${label} results`}>
          {state.loading ? (
            <div className="pr-results-note">Searching…</div>
          ) : state.error ? (
            <div className="pr-results-note pc-err">{state.error}</div>
          ) : state.items.length === 0 ? (
            <div className="pr-results-note">{emptyText}</div>
          ) : (
            state.items.map((item) => (
              <button type="button" role="option" aria-selected="false" key={item.id} className="pr-result" onClick={() => onPick(item)}>
                {render(item)}
              </button>
            ))
          )}
        </div>
      )}
    </>
  );
}

export default function NewRequestDialog({ onClose, onCreated }) {
  const dialogRef = useRef(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [saving, setSaving] = useState(false);
  const [orgQuery, setOrgQuery] = useState('');
  const [payerQuery, setPayerQuery] = useState('');
  const [events, setEvents] = useState({ items: [], loading: false, error: '' });
  useDialogFocus(dialogRef, onClose, saving);
  // Picking (or un-picking) unmounts the focused control; move focus to the
  // control that replaces it so the dialog keeps keyboard focus (Escape, Tab).
  const [focusTarget, setFocusTarget] = useState(null);
  useEffect(() => {
    if (!focusTarget) return;
    const el = dialogRef.current && dialogRef.current.querySelector(`#${focusTarget}`);
    if (el) el.focus();
    setFocusTarget(null);
  }, [focusTarget]);

  const findOrganizers = useMemo(() => async (q) => (await searchOrganizers(q)).organizers || [], []);
  const findPayers = useMemo(() => async (q) => (await searchPayers(q)).payers || [], []);
  const orgSearch = useSearch(findOrganizers, orgQuery, { enabled: !form.organizer });
  const payerSearch = useSearch(findPayers, payerQuery, { minLength: 2, enabled: !form.payer });

  // the organizer's events (and their payout accounts)
  const organizerId = form.organizer?.id;
  useEffect(() => {
    if (!organizerId) {
      setEvents({ items: [], loading: false, error: '' });
      return undefined;
    }
    let live = true;
    setEvents({ items: [], loading: true, error: '' });
    organizerEvents(organizerId)
      .then((res) => live && setEvents({ items: res.events || [], loading: false, error: '' }))
      .catch((e) => live && setEvents({ items: [], loading: false, error: errText(e) }));
    return () => {
      live = false;
    };
  }, [organizerId]);

  const event = events.items.find((e) => eventKeyOf(e) === form.eventKey) || null;
  const feeRule = feeRuleOf(event);
  const currency = event?.currency || 'cad';
  const totals = previewTotals(form);

  const set = (patch) => {
    setErrors((cur) => {
      const next = { ...cur };
      Object.keys(patch).forEach((k) => {
        delete next[k];
        if (k === 'eventKey') delete next.event;
      });
      return next;
    });
    setServerError('');
    setForm((cur) => {
      const next = { ...cur, ...patch };
      // until the admin edits the fee, it follows the amount (and the event's rate)
      if (!next.feeTouched && ('amount' in patch || 'eventKey' in patch)) {
        const cents = parseDollarsToCents(next.amount);
        const ev = events.items.find((e) => eventKeyOf(e) === next.eventKey);
        next.fee = cents === null ? '' : centsToInput(defaultFeeCents(cents, feeRuleOf(ev)));
      }
      return next;
    });
  };

  const resetFee = () => {
    const cents = parseDollarsToCents(form.amount);
    set({ feeTouched: false, fee: cents === null ? '' : centsToInput(defaultFeeCents(cents, feeRule)) });
  };

  const send = async () => {
    const { errors: errs, payload } = validateForm(form, events.items);
    if (!payload) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    setServerError('');
    try {
      const res = await createRequest(payload);
      onCreated(res);
    } catch (e) {
      setServerError(errText(e));
      setSaving(false);
    }
  };

  const noteLength = form.note.trim().length;

  return (
    <div className="modal-scrim" role="presentation">
      <div className="omodal ds-modal pr-modal" role="dialog" aria-modal="true" aria-labelledby="pr-new-title" ref={dialogRef} tabIndex={-1}>
        <div className="ds-modal-h">
          <div className="op-title" id="pr-new-title" role="heading" aria-level="2">New payment request</div>
          <p className="op-note">We’ll email the payer a link. They log in as themselves and pay by card.</p>
        </div>
        <div className="ds-modal-b">
          <Field label="Organizer" error={errors.organizer} htmlFor="pr-org">
            {form.organizer ? (
              <Picked
                icon="corporate_fare"
                title={form.organizer.name}
                sub={`organizer · ${form.organizer.email}`}
                label="organizer"
                id="pr-org-change"
                onChange={() => {
                  set({ organizer: null, eventKey: '' });
                  setFocusTarget('pr-org');
                }}
              />
            ) : (
              <Picker
                id="pr-org"
                label="Search organizers"
                placeholder="Search organizers by name or email"
                query={orgQuery}
                setQuery={setOrgQuery}
                state={orgSearch}
                autoFocus
                emptyText="No organizers match."
                onPick={(o) => {
                  set({ organizer: o, eventKey: '' });
                  setFocusTarget('pr-org-change');
                }}
                render={(o) => (
                  <span className="pr-who"><b>{o.name}</b><span>{o.email}</span></span>
                )}
              />
            )}
          </Field>

          {form.organizer && (
            <Field
              label="Event"
              error={errors.event}
              htmlFor="pr-event"
              hint={events.loading ? 'Loading events…' : events.error || (events.items.length === 0 ? 'This organizer has no events.' : '')}
            >
              <select
                id="pr-event"
                className="ds-select pr-full"
                value={form.eventKey}
                onChange={(e) => set({ eventKey: e.target.value })}
                disabled={events.loading || events.items.length === 0}
              >
                <option value="">Choose the event…</option>
                {events.items.map((e) => (
                  <option key={eventKeyOf(e)} value={eventKeyOf(e)} disabled={!e.account?.assigned}>
                    {e.name} · {EVENT_TYPE_LABELS[e.type] || e.type}
                    {e.date ? ` · ${dueDay(e.date)}` : ''}
                    {e.account?.assigned ? '' : ' · no payout account'}
                  </option>
                ))}
              </select>
              {event?.account?.assigned && (
                <div className="pr-acct">
                  <Msr>account_balance</Msr>
                  Pays out to <b>{event.account.name || 'the organizer’s Stripe account'}</b>
                </div>
              )}
            </Field>
          )}

          <Field
            label="Payer"
            error={errors.payer}
            htmlFor="pr-payer"
            hint="Search any Feis.link account by name or email. They must pay while logged in as this account."
          >
            {form.payer ? (
              <Picked
                icon="person"
                title={form.payer.name}
                sub={[form.payer.email, form.payer.teacher ? 'teacher' : '', form.payer.school || ''].filter(Boolean).join(' · ')}
                label="payer"
                id="pr-payer-change"
                onChange={() => {
                  set({ payer: null });
                  setFocusTarget('pr-payer');
                }}
              />
            ) : (
              <Picker
                id="pr-payer"
                label="Search payers"
                placeholder="Name or email (at least 2 letters)"
                query={payerQuery}
                setQuery={setPayerQuery}
                state={payerSearch}
                minLength={2}
                emptyText="No accounts match."
                onPick={(p) => {
                  set({ payer: p });
                  setFocusTarget('pr-payer-change');
                }}
                render={(p) => (
                  <span className="pr-who">
                    <b>{p.name}</b>
                    <span>{[p.email, p.teacher ? 'teacher' : '', p.school || ''].filter(Boolean).join(' · ')}</span>
                  </span>
                )}
              />
            )}
          </Field>

          <div className="ds-row2">
            <Field label="Amount for the organizer" error={errors.amount} htmlFor="pr-amount">
              <div className="pc-input">
                <span>$</span>
                <input
                  id="pr-amount"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => set({ amount: e.target.value })}
                />
              </div>
            </Field>
            <Field label="Platform fee" error={errors.fee} htmlFor="pr-fee">
              <div className="pc-input">
                <span>$</span>
                <input
                  id="pr-fee"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={form.fee}
                  onChange={(e) => set({ fee: e.target.value, feeTouched: true })}
                />
              </div>
              {!errors.fee && (
                <div className="pr-feehint ds-hint">
                  {form.feeTouched ? (
                    <>
                      Set by hand.{' '}
                      <button type="button" onClick={resetFee}>Reset to this event’s fee</button>
                    </>
                  ) : (
                    <>{event ? `This event’s fee: ${feeRule.label}` : feeRule.label}</>
                  )}
                </div>
              )}
            </Field>
          </div>

          <div className="pr-total pay-body" aria-live="polite">
            <div className="prow">
              <span className="k">To {(event?.account?.assigned && event.account.name) || form.organizer?.name || 'the organizer'}</span>
              <span className="v">{money(totals.organizerCents, currency)}</span>
            </div>
            <div className="prow">
              <span className="k">Platform fee</span>
              <span className="v">{money(totals.feeCents, currency)}</span>
            </div>
            <div className="prow total">
              <span className="k">Payer is charged</span>
              <span className="v">
                {money(totals.totalCents, currency)}
                <span className="cur">{currency.toUpperCase()}</span>
              </span>
            </div>
          </div>

          <Field
            label="What it’s for"
            error={errors.note}
            htmlFor="pr-note"
            hint={`Shown to the payer, on their receipt and to the organizer. ${NOTE_MAX} characters max · ${noteLength} used.`}
          >
            <textarea
              id="pr-note"
              className="ds-input pr-text"
              maxLength={NOTE_MAX}
              value={form.note}
              onChange={(e) => set({ note: e.target.value })}
            />
          </Field>

          <div className="pr-due">
            <Field
              label={<>Pay by <span className="op-muted">(optional)</span></>}
              error={errors.dueAt}
              htmlFor="pr-due"
              hint="Shown in the email; the link keeps working after it."
            >
              <input id="pr-due" className="ds-input" type="date" value={form.dueAt} onChange={(e) => set({ dueAt: e.target.value })} />
            </Field>
          </div>

          {serverError && <div className="op-error" role="alert">{serverError}</div>}
        </div>
        <div className="ds-modal-act pr-act">
          {form.payer && (
            <span className="pr-emailto">
              <Msr>mail</Msr>Emails {form.payer.email}
            </span>
          )}
          <button type="button" className="mbtn ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="ab-next" onClick={send} disabled={saving}>
            <Msr>send</Msr>
            {saving ? 'Sending…' : 'Send request'}
          </button>
        </div>
      </div>
    </div>
  );
}
